/**
 * Passphrase-sealed KeyStore wrapper.
 *
 * Fixes HIGH-06: identity private keys were previously stored plaintext
 * in IndexedDB, so any XSS or browser-filesystem access could exfiltrate
 * the user's identity. Now every value we persist (identity keypairs,
 * MLS group state, KeyPackage pool) is AES-256-GCM sealed under a
 * passphrase-derived Argon2id key.
 *
 * Storage layout on the underlying KeyStore (IndexedDB):
 *   - `chat:salt:v1`    — 16 random bytes, unsealed. Lets us derive the
 *                         same key across reloads.
 *   - every other key   — sealed value (nonce + ciphertext + AAD-bound
 *                         key name). The SealedKeyStore adds AAD derived
 *                         from the key name so a ciphertext can't be
 *                         moved between slots.
 *
 * User flow:
 *   - Fresh browser, no salt yet: prompt for a NEW passphrase. Generate
 *     salt. Derive key. All subsequent writes are sealed.
 *   - Returning user, salt present: prompt for the existing passphrase.
 *     Derive key. Attempt to load sealed identity — `open()` throws on
 *     wrong passphrase, which we surface as "incorrect passphrase".
 */
import { deriveKey, generateSalt, SealedKeyStore, type KeyStore } from '@chat/crypto';

export const SALT_KEY = 'chat:salt:v1';

export async function readSalt(raw: KeyStore): Promise<Uint8Array | undefined> {
  return raw.get(SALT_KEY);
}

export async function openSealedStore(
  raw: KeyStore,
  passphrase: string,
  opts: { createIfMissing?: boolean } = {},
): Promise<{ sealed: KeyStore; isNew: boolean }> {
  if (!passphrase || passphrase.length < 8) {
    throw new Error('passphrase must be at least 8 characters');
  }
  let salt = await raw.get(SALT_KEY);
  let isNew = false;
  if (!salt) {
    if (!opts.createIfMissing) {
      throw new Error('no salt — first-time unlock requires createIfMissing: true');
    }
    salt = generateSalt();
    await raw.put(SALT_KEY, salt);
    isNew = true;
  }
  const derived = await deriveKey(passphrase, salt);
  return { sealed: new SealedKeyStore(raw, derived), isNew };
}

/**
 * Attempts to unlock a sealed store, then sanity-reads any known record
 * to verify the passphrase is correct. Returns the sealed store on
 * success; throws on a bad passphrase (the AES-GCM tag check fires).
 */
export async function unlockSealedStore(
  raw: KeyStore,
  passphrase: string,
  probeKey: string,
): Promise<KeyStore> {
  const { sealed } = await openSealedStore(raw, passphrase);
  // Read the probe key. If decrypt fails (wrong passphrase), the error
  // propagates. If the probe doesn't exist, we accept — the store is
  // valid but empty for that key, e.g. after a claim on a device where
  // the salt was set but the identity hasn't been saved yet.
  try {
    await sealed.get(probeKey);
  } catch (err) {
    // Any failure coming out of `@chat/crypto`'s `open` (AES-GCM
    // decrypt, sealed-blob too short, stored-key-name mismatch —
    // effectively "the bytes under this slot cannot be unsealed with
    // this key") means the passphrase or the stored data don't line
    // up. We surface a uniform "incorrect passphrase" to avoid UI
    // branching on crypto internals (LOW-21). Storage errors
    // (indexedDB unavailable, quota, etc.) still propagate untouched.
    if (isSealDecryptFailure(err)) {
      throw new Error('incorrect passphrase');
    }
    throw err;
  }
  return sealed;
}

function isSealDecryptFailure(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /decrypt|auth|tag|operation|GCM|cipher|sealed blob|key name mismatch/i.test(err.message);
}
